/**
 * Scans a comment body for `attachment:<uuid>` references and returns the unique set of file ids
 * in order of first appearance.
 *
 * The composer and edit form need to populate `attachment_ids` on create/edit alongside the
 * markdown body; rather than carry a separate uploaded-id state that must be kept in sync with
 * the editor's content (drag-out / Backspace / paste-replace would all need bookkeeping), we
 * derive the list straight from the body — that guarantees the server never sees a stale id and
 * matches the backend's own extractor on the read path.
 *
 * The regex matches the same lower-cased canonical UUID shape Tiptap inserts (we always write
 * `src='attachment:<uuid>'` from the upload service) but is case-insensitive defensively so a
 * user pasting in markdown by hand doesn't get a surprise rejection.
 */
const ATTACHMENT_TOKEN_REGEX =
  /attachment:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g;

export function extractAttachmentIds(bodyMarkdown: string): readonly string[] {
  ATTACHMENT_TOKEN_REGEX.lastIndex = 0;
  const seen = new Set<string>();
  const out: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = ATTACHMENT_TOKEN_REGEX.exec(bodyMarkdown)) !== null) {
    const id = match[1]?.toLowerCase();
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}
